import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Loading from "../../components/loading";
import Pagination from "../../components/Pagination";
import ModalMoslash from "../../components/akfa/ModalMoslash";
import {
    AKFA_PAGE_BASE,
    exportAkfaUnmappedProducts,
    getAkfaProducts,
    ignoreAkfaProducts,
    markAkfaProductSeen,
} from "../../services/akfa/akfaService";
import {
    AKFA_PAGE_SIZE,
    SELECT_CLASS,
    downloadBlob,
    formatDateTime,
    formatPrice,
    showError,
    showSuccess,
    toPageState,
} from "./akfaUtils";

export default function AkfaNewProductsTab({ refreshSummary }) {
    const { t } = useTranslation();
    const { token } = useSelector((state) => state.user);

    const [pageState, setPageState] = useState({ items: [], total: 0, pageCount: 0 });
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [filterIsNew, setFilterIsNew] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [filterSearch, setFilterSearch] = useState("");
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [modal, setModal] = useState({ open: false, akfaProduct: null });
    const [ignoreModal, setIgnoreModal] = useState({ open: false, ids: [] });
    const [ignoreReason, setIgnoreReason] = useState("");
    const [exporting, setExporting] = useState(false);
    const selectAllRef = useRef(null);

    const fetchData = async () => {
        setIsLoading(true);
        const response = await getAkfaProducts(token, {
            source: "PRODUCT",
            isNew: filterIsNew,
            search: filterSearch,
            page: page + AKFA_PAGE_BASE,
            size: AKFA_PAGE_SIZE,
        });
        if (response.success) {
            setPageState(toPageState(response.data, AKFA_PAGE_SIZE));
        } else {
            showError(response, t("akfaIntegrationPage.common.load_error"));
        }
        setIsLoading(false);
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, filterIsNew, filterSearch]);

    useEffect(() => {
        setSelectedIds(new Set());
    }, [page, filterIsNew, filterSearch]);

    const items = pageState.items;
    const isAllSelected = items.length > 0 && items.every((item) => selectedIds.has(item.id));
    const isIndeterminate = !isAllSelected && items.some((item) => selectedIds.has(item.id));

    useEffect(() => {
        if (selectAllRef.current) selectAllRef.current.indeterminate = isIndeterminate;
    }, [isIndeterminate]);

    const handleSelectAll = () => {
        setSelectedIds(isAllSelected ? new Set() : new Set(items.map((item) => item.id)));
    };

    const handleSelectOne = (id) => {
        const next = new Set(selectedIds);
        next.has(id) ? next.delete(id) : next.add(id);
        setSelectedIds(next);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(0);
        setFilterSearch(searchInput.trim());
    };

    const refresh = () => {
        fetchData();
        refreshSummary();
    };

    const openIgnore = (ids) => {
        setIgnoreReason("");
        setIgnoreModal({ open: true, ids });
    };

    const closeIgnoreModal = () => setIgnoreModal({ open: false, ids: [] });

    const submitIgnore = async () => {
        const response = await ignoreAkfaProducts(token, { ids: ignoreModal.ids, reason: ignoreReason.trim() });
        if (response.success) {
            const ignored = response.data?.ignored || [];
            const skipped = response.data?.skipped || [];
            if (ignored.length > 0) {
                showSuccess(t("akfaIntegrationPage.newProducts.ignore_success", { count: ignored.length }));
            }
            skipped.forEach((s) =>
                toast.warn(`#${s.id}: ${s.reason}`, { position: "bottom-right", autoClose: 6000 })
            );
        } else {
            showError(response, t("akfaIntegrationPage.common.action_error"));
        }
        setSelectedIds(new Set());
        closeIgnoreModal();
        refresh();
    };

    const openMoslash = (item) => {
        setModal({ open: true, akfaProduct: item });
        if (item.isNew) {
            markAkfaProductSeen(token, item.id).then((response) => {
                if (!response.success) return;
                setPageState((s) => ({
                    ...s,
                    items: s.items.map((it) => (it.id === item.id ? { ...it, isNew: false } : it)),
                }));
                refreshSummary();
            });
        }
    };

    const handleExport = async () => {
        setExporting(true);
        const response = await exportAkfaUnmappedProducts(token);
        setExporting(false);
        if (response.success) {
            downloadBlob(response.data, response.fileName);
        } else {
            showError(response, t("akfaIntegrationPage.common.action_error"));
        }
    };

    return (
        <div className="py-4">
            <div className="mb-4 flex flex-wrap items-center gap-3">
                <select
                    value={filterIsNew}
                    onChange={(e) => {
                        setPage(0);
                        setFilterIsNew(e.target.value);
                    }}
                    className={SELECT_CLASS}
                >
                    <option value="">{t("akfaIntegrationPage.newProducts.filter_all")}</option>
                    <option value="true">{t("akfaIntegrationPage.newProducts.filter_unseen")}</option>
                </select>

                <form onSubmit={handleSearch} className="flex gap-2">
                    <input
                        type="text"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder={t("akfaIntegrationPage.common.search_placeholder")}
                        className={SELECT_CLASS}
                    />
                    <button type="submit" className="px-4 py-2 bg-gray-700 text-white text-sm rounded-md hover:bg-gray-600">
                        {t("akfaIntegrationPage.common.search")}
                    </button>
                </form>

                <div className="ml-auto flex items-center gap-3">
                    {selectedIds.size > 0 && (
                        <>
                            <span className="text-sm text-gray-600">
                                {t("akfaIntegrationPage.common.selected_count", { count: selectedIds.size })}
                            </span>
                            <button
                                onClick={() => openIgnore([...selectedIds])}
                                className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-md hover:bg-gray-400"
                            >
                                {t("akfaIntegrationPage.newProducts.bulk_ignore")}
                            </button>
                        </>
                    )}
                    <button
                        onClick={handleExport}
                        disabled={exporting}
                        className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 disabled:opacity-60"
                    >
                        {exporting ? t("akfaIntegrationPage.common.loading") : t("akfaIntegrationPage.newProducts.export_excel")}
                    </button>
                </div>
            </div>

            {isLoading ? (
                <Loading />
            ) : (
                <>
                    <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                        <table className="min-w-full divide-y divide-gray-300">
                            <thead className="text-white bg-gray-700">
                                <tr>
                                    <th className="py-3 pl-4 pr-2 w-10">
                                        {items.length > 0 && (
                                            <input
                                                ref={selectAllRef}
                                                type="checkbox"
                                                checked={isAllSelected}
                                                onChange={handleSelectAll}
                                                className="h-4 w-4 rounded border-gray-400 text-indigo-600 cursor-pointer"
                                            />
                                        )}
                                    </th>
                                    <th className="py-3 pl-2 pr-3 text-left text-sm font-semibold">
                                        {t("akfaIntegrationPage.newProducts.col_name")}
                                    </th>
                                    <th className="py-3 pl-2 pr-3 text-left text-sm font-semibold">
                                        {t("akfaIntegrationPage.newProducts.col_group")}
                                    </th>
                                    <th className="py-3 pl-2 pr-3 text-left text-sm font-semibold">
                                        {t("akfaIntegrationPage.newProducts.col_unit")}
                                    </th>
                                    <th className="py-3 pl-2 pr-3 text-left text-sm font-semibold">
                                        {t("akfaIntegrationPage.newProducts.col_price")}
                                    </th>
                                    <th className="py-3 pl-2 pr-3 text-left text-sm font-semibold">
                                        {t("akfaIntegrationPage.newProducts.col_first_seen")}
                                    </th>
                                    <th className="relative py-3 pl-3 pr-4">
                                        <span className="sr-only">{t("akfaIntegrationPage.common.actions")}</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-300">
                                {items.length > 0 ? (
                                    items.map((item) => (
                                        <tr
                                            key={item.id}
                                            className={`hover:bg-gray-50 ${selectedIds.has(item.id) ? "bg-indigo-50" : ""}`}
                                        >
                                            <td className="py-2 pl-4 pr-2">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.has(item.id)}
                                                    onChange={() => handleSelectOne(item.id)}
                                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 cursor-pointer"
                                                />
                                            </td>
                                            <td className="px-2 py-2 text-sm text-gray-700">
                                                <div className="font-medium text-gray-900 flex items-center gap-2">
                                                    {item.name}
                                                    {item.isNew && (
                                                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700">
                                                            {t("akfaIntegrationPage.newProducts.new_badge")}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-gray-400">
                                                    #{item.externalId} · {item.externalStatus}
                                                </div>
                                            </td>
                                            <td className="px-2 py-2 text-sm text-gray-700">{item.groupName || "—"}</td>
                                            <td className="px-2 py-2 text-sm text-gray-700">{item.unitName || "—"}</td>
                                            <td className="px-2 py-2 text-sm text-gray-700">
                                                {formatPrice(item.currentRate, item.currentRateCurrency)}
                                            </td>
                                            <td className="px-2 py-2 text-sm text-gray-700">
                                                {formatDateTime(item.firstSeenAt || item.externalCreatedDate)}
                                            </td>
                                            <td className="relative py-2 text-right text-sm font-medium pr-4">
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        onClick={() => openMoslash(item)}
                                                        className="px-3 py-1 bg-indigo-600 text-white rounded-md text-xs hover:bg-indigo-500"
                                                    >
                                                        {t("akfaIntegrationPage.newProducts.map_button")}
                                                    </button>
                                                    <button
                                                        onClick={() => openIgnore([item.id])}
                                                        className="px-3 py-1 bg-gray-200 text-gray-700 rounded-md text-xs hover:bg-gray-300"
                                                    >
                                                        {t("akfaIntegrationPage.newProducts.ignore_button")}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={7} className="py-8 text-center text-sm text-gray-500">
                                            {t("akfaIntegrationPage.newProducts.empty")}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {pageState.pageCount > 1 && (
                        <Pagination
                            handlePageClick={(e) => setPage(e.selected)}
                            pageCount={pageState.pageCount}
                            forcePage={page}
                        />
                    )}
                </>
            )}

            {/* Yopilganda to'liq unmount — aks holda Transition'ning ko'rinmas `fixed inset-0` qatlami sahifadagi bosishlarni to'sib qoladi */}
            {modal.open && (
                <ModalMoslash
                    open={modal.open}
                    setOpen={(v) => setModal((s) => (v ? { ...s, open: true } : { open: false, akfaProduct: null }))}
                    akfaProduct={modal.akfaProduct}
                    onSaved={refresh}
                />
            )}

            {ignoreModal.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            {ignoreModal.ids.length > 1
                                ? t("akfaIntegrationPage.newProducts.ignore_modal_title_bulk", {
                                      count: ignoreModal.ids.length,
                                  })
                                : t("akfaIntegrationPage.newProducts.ignore_modal_title")}
                        </h3>
                        <textarea
                            value={ignoreReason}
                            onChange={(e) => setIgnoreReason(e.target.value)}
                            rows={3}
                            placeholder={t("akfaIntegrationPage.newProducts.ignore_reason_placeholder")}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
                        />
                        <div className="mt-4 flex justify-end gap-3">
                            <button
                                onClick={closeIgnoreModal}
                                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                {t("akfaIntegrationPage.common.cancel")}
                            </button>
                            <button
                                onClick={submitIgnore}
                                className="px-4 py-2 text-sm text-white bg-gray-700 rounded-md hover:bg-gray-600"
                            >
                                {t("akfaIntegrationPage.newProducts.ignore_button")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
